import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import ProjectList from './components/ProjectList';
import ProjectModal from './components/ProjectModal';
import BaseLayout from '../../layouts/BaseLayout';
import { useApiHandler } from '../../utils/apiHandler';

function ProjectPage() {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]); // New state for employees
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true); // To show loading spinner or message
  const [error, setError] = useState(null); // To handle and display any errors
  const apiHandler = useApiHandler();
  const [selectedProject, setSelectedProject] = useState(null); // To handle editing

  // Fetch projects on component load
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await apiHandler.get('/admin/get-project'); // Replace with your API endpoint
        const { data } = response;
        if (data.success) {
          setProjects(data.data.projects); // Assuming response has { data: { projects: [] } }
        } else {
          setError(data.message || 'Failed to fetch projects.');
        }
      } catch (err) {
        setError(err.message || 'An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Fetch employees on component load
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await apiHandler.get('/admin/employee-lists'); // Replace with your API endpoint
        const { data } = response;
        if (data.success) {
          setEmployees(data.data); // Store employee list in the state
        } else {
          setError(data.message || 'Failed to fetch employees.');
        }
      } catch (err) {
        setError(err.message || 'An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Handle adding a new project
  const handleAddProject = async (newProject) => {
    try {
      const response = await apiHandler.post('/admin/project-assign', {
        projectName: newProject.name,
        projectDescription: newProject.description,
      });
      const { data } = response;
      if (data.success) {
        console.log(data?.data)
        setProjects([...projects, data.data]);
        setIsModalOpen(false);
      } else {
        alert(data.message || 'Failed to create project. Please try again.');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project. Please try again.');
    }
  };

  // Handle updating a project
  const handleUpdateProject = async (updatedProject) => {
    try {
      const response = await apiHandler.post('/admin/update-project', {
        projectId: updatedProject._id, // Assuming project has _id field
        projectName: updatedProject.projectName,
        projectDescription: updatedProject.projectDescription,
        employees: updatedProject.employees,
      });

      const { data } = response;
      if (data.success) {
        // Update the local project list with the updated project
        setProjects((prevProjects) =>
          prevProjects.map((project) =>
            project._id === updatedProject._id ? { ...project, ...updatedProject } : project
          )
        );
        setIsModalOpen(false); // Close modal after success
      } else {
        alert(data.message || 'Failed to update project. Please try again.');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project. Please try again.');
    }
  };

  const handleProjectDelete = async (projectId) => {
    try {
      const response = await apiHandler.delete(`/admin/delete-project/${projectId}`); // Replace with your API endpoint
      const { data } = response;
      if (data.success) {
        setProjects((prevProjects) => prevProjects.filter((project) => project._id !== projectId));
        alert('Project deleted successfully.');
      } else {
        alert(data.message || 'Failed to delete project.');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };
  


  // Handle editing a project
  const handleEditProject = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true); // Open modal for editing
  };

  return (
    <BaseLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
              <p className="text-gray-600 mt-2">Manage your projects and assign tasks</p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Project
            </button>
          </div>

          {loading ? (
            <p className="text-gray-600 text-center">Loading projects...</p>
          ) : error ? (
            <p className="text-red-600 text-center">{error}</p>
          ) : projects.length === 0 ? (
            <p className="text-gray-500 text-center">No projects available. Start by creating one!</p>
          ) : (
            <ProjectList
              projects={projects}
              employeeData={employees}
              onDelete={handleProjectDelete}
              onUpdateProject={handleUpdateProject}
              onEditProject={handleEditProject} // Pass edit handler
            />
          )}

          {isModalOpen && (
            <ProjectModal
              project={selectedProject}
              onClose={() => setIsModalOpen(false)}
              onSubmit={handleAddProject}
              onUpdate={handleUpdateProject} // Pass update handler to modal
              employees={employees}
            />
          )}
        </div>
      </div>
    </BaseLayout>
  );
}

export default ProjectPage;
